import { Controller } from "@hotwired/stimulus"
import { translate, browserLocale, formatNumber, lastValue, max, formatMemory, formatStep, sparkline, escapeHtml } from "./load_format"

// ABox load tab (a_boxes/pills/_load): current value tiles for the last 24 hours and a uPlot chart per unit.
// The charts share one time axis: synced crosshair, drag over any of them to load the selected period
// (the box returns a finer resolution, down to 5 seconds).
const CHARTS = {
  cpu:     { unit: 'percent', min: 0, max: 100, series: [{ key: 'cpu', color: 's1', band: true }, { key: 'cpu_iowait', color: 's2' }], offlineLabel: true },
  memory:  { unit: 'percent', min: 0, max: 100, series: [{ key: 'ram', color: 's1' }, { key: 'swap', color: 's2' }] },
  temp:    { unit: 'celsius', min: 30, series: [{ key: 'temp', color: 's1' }], throttling: true },
  network: { unit: 'mbit', min: 0, series: [{ key: 'net_rx', color: 's1' }, { key: 'net_tx', color: 's2' }] },
  disk:    { unit: 'mb', min: 0, series: [{ key: 'disk_write', color: 's1' }, { key: 'disk_read', color: 's2' }] },
  load:    { unit: null, min: 0, series: [{ key: 'load', color: 's1' }], cores: true }
}
// Digits in legends and tooltips
const DIGITS = { cpu: 1, cpu_max: 1, cpu_iowait: 1, ram: 1, swap: 1, temp: 1, load: 2, net_rx: 1, net_tx: 1, disk_read: 1, disk_write: 1 }
const UNITS = { cpu: '%', cpu_max: '%', cpu_iowait: '%', ram: '%', swap: '%', temp: '°C' }
const KEYS = ['cpu', 'cpu_max', 'cpu_iowait', 'ram', 'swap', 'temp', 'cpu_freq', 'load', 'net_rx', 'net_tx', 'disk_read', 'disk_write']
const TILES_PERIOD = 24 * 3600
const CHART_HEIGHT = 220
const MIN_SELECTION_PX = 5
// Throttling: a busy CPU below this part of the period's top frequency (an idle one is slowed down by the governor)
const THROTTLED_FREQ = 0.9
const BUSY_CPU = 50

export default class extends Controller {
  static targets = ['tiles', 'chart', 'legend', 'status', 'preset', 'from', 'to']
  static values  = { url: String, period: Number, labels: Object, locale: String }

  connect() {
    this.plots = {}
    this.dark  = this.isDark()

    // Tab panes are hidden until opened: fetch and draw once the tab is visible
    this.visibility = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      this.visibility.disconnect()
      this.start()
    })
    this.visibility.observe(this.element)

    // The theme toggle (application_controller) switches body.dark-theme without a reload
    this.theme = new MutationObserver(() => {
      if (this.isDark() === this.dark) return
      this.dark = this.isDark()
      if (this.history) this.renderCharts(this.history)
    })
    this.theme.observe(document.body, { attributes: true, attributeFilter: ['class'] })

    this.resize = new ResizeObserver(() => this.resizeCharts())
    this.chartTargets.forEach((target) => this.resize.observe(target))
  }

  disconnect() {
    this.visibility.disconnect()
    this.theme.disconnect()
    this.resize.disconnect()
    this.abort?.abort()
    this.tilesAbort?.abort()
    this.destroyCharts()
  }

  // uPlot (and its stylesheet) is loaded only here, not with every page
  async start() {
    const [uPlot] = await Promise.all([
      import(/* webpackChunkName: "uplot" */ 'uplot'),
      import(/* webpackChunkName: "uplot" */ 'uplot/dist/uPlot.min.css')
    ])
    this.uPlot = uPlot.default
    this.loadTiles()
    this.selectPeriodSeconds(this.periodValue)
  }

  selectPeriod(event) {
    this.presetTargets.forEach((button) => button.classList.toggle('active', button === event.currentTarget))
    this.selectPeriodSeconds(Number(event.currentTarget.dataset.period))
  }

  selectRange() {
    const from = this.parseDate(this.fromTarget.value)
    const to   = this.parseDate(this.toTarget.value) || this.now()
    if (!from || from >= to) return

    this.presetTargets.forEach((button) => button.classList.remove('active'))
    this.load(from, to)
  }

  selectPeriodSeconds(period) {
    const to = this.now()
    this.load(to - period, to)
  }

  async load(from, to) {
    this.abort?.abort()
    this.abort = new AbortController()
    this.statusTarget.textContent = this.labelsValue.loading

    try {
      this.history = await this.fetchHistory(from, to, this.abort.signal)
      this.renderCharts(this.history)
    } catch (error) {
      if (error.name !== 'AbortError') this.statusTarget.textContent = this.labelsValue.failed
    }
  }

  async loadTiles() {
    this.tilesAbort = new AbortController()
    const to = this.now()

    try {
      this.renderTiles(await this.fetchHistory(to - TILES_PERIOD, to, this.tilesAbort.signal))
    } catch (error) {
      if (error.name !== 'AbortError') this.tilesTarget.hidden = true
    }
  }

  async fetchHistory(from, to, signal) {
    const response = await fetch(`${this.urlValue}?from=${Math.floor(from)}&to=${Math.floor(to)}`, { headers: { Accept: 'application/json' }, signal })
    if (!response.ok) throw new Error(response.status)

    return response.json()
  }

  // Tiles

  renderTiles(data) {
    const labels = this.labelsValue
    const locale = this.localeValue
    const totals = data.totals || {}
    const { cols } = this.withGaps(data)
    const number = (key, digits = 1) => formatNumber(lastValue(data, key), digits, locale)
    const peak   = (key) => formatNumber(max(data[`${key}_max`] || data[key]), 1, locale)

    const tiles = [
      { label: labels.tiles.cpu, value: number('cpu'), unit: '%', context: translate(labels.tiles.peak, { value: `${peak('cpu')}%` }), values: cols.cpu, min: 0, max: 100 },
      this.memoryTile(labels.tiles.ram, lastValue(data, 'ram'), totals.ram_mb, cols.ram),
      this.memoryTile(labels.tiles.swap, lastValue(data, 'swap'), totals.swap_mb, cols.swap),
      {
        label: labels.tiles.temp, value: number('temp'), unit: '°C', context: translate(labels.tiles.peak, { value: `${peak('temp')}°C` }),
        values: cols.temp, min: 30, max: Math.max(80, max(cols.temp) || 0), warning: this.throttled(data).length ? labels.tiles.throttling : null
      },
      {
        label: labels.tiles.network, value: number('net_rx'), unit: labels.units.mbit,
        context: translate(labels.tiles.out, { value: `${number('net_tx')} ${labels.units.mbit}` }), values: cols.net_rx, min: 0, max: max(cols.net_rx) || 1
      },
      {
        label: labels.tiles.disk, value: number('disk_write'), unit: labels.units.mb,
        context: `${labels.tiles.write} · ${translate(labels.tiles.read, { value: `${number('disk_read')} ${labels.units.mb}` })}`,
        values: cols.disk_write, min: 0, max: max(cols.disk_write) || 1
      }
    ]

    this.tilesTarget.innerHTML = tiles.map((tile) => this.tileHtml(tile)).join('')
  }

  // Used memory in GB/MB of the box total, the percent when the box didn't send totals
  memoryTile(label, percent, totalMb, values) {
    const labels = this.labelsValue
    const locale = this.localeValue
    const tile   = { label, values, min: 0, max: 100 }
    if (percent == null || !totalMb) return { ...tile, value: formatNumber(percent, 1, locale), unit: '%', context: '' }

    const [value, unit] = formatMemory(percent / 100 * totalMb, labels, locale).split(' ')
    return { ...tile, value, unit, context: `${translate(labels.tiles.of_total, { total: formatMemory(totalMb, labels, locale) })} · ${formatNumber(percent, 0, locale)}%` }
  }

  tileHtml(tile) {
    const labels = this.labelsValue
    const { line, area } = sparkline(tile.values || [], tile.min, tile.max)
    const warning = tile.warning
      ? `<span class="abox-load__warning"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i>${escapeHtml(tile.warning)}</span>`
      : ''

    return `
      <article class="abox-load__tile">
        <div class="abox-load__tile-label"><span>${escapeHtml(tile.label)}</span>${warning}</div>
        <div class="abox-load__tile-value">${escapeHtml(tile.value)}<small>${escapeHtml(tile.unit)}</small></div>
        <div class="abox-load__tile-context">${escapeHtml(tile.context)}</div>
        <svg viewBox="0 0 100 32" preserveAspectRatio="none" role="img" aria-label="${escapeHtml(`${tile.label}: ${labels.day_ago} — ${labels.present}`)}">
          <path d="${area}" style="fill: var(--chart-s1-fill)"></path>
          <path d="${line}" fill="none" style="stroke: var(--chart-s1)" stroke-width="1.5" vector-effect="non-scaling-stroke" stroke-linejoin="round"></path>
        </svg>
        <div class="abox-load__tile-axis"><span>${escapeHtml(labels.day_ago)}</span><span>${escapeHtml(labels.present)}</span></div>
      </article>`
  }

  // Charts

  renderCharts(data) {
    this.destroyCharts()
    const labels = this.labelsValue

    if (!data.t.length) {
      this.statusTarget.textContent = labels.no_data
      this.legendTargets.forEach((legend) => { legend.innerHTML = '' })
      return
    }

    const colors    = this.colors()
    const series    = this.withGaps(data)
    const throttled = this.throttled(data)

    this.chartTargets.forEach((target) => {
      const name   = target.dataset.chart
      const config = CHARTS[name]
      const plot   = this.plotSeries(config, data, series)

      this.renderLegend(name, config, data, throttled)
      this.plots[name] = new this.uPlot(this.options(name, config, data, series, plot, throttled, colors), [series.t, ...plot.map((item) => item.values)], target)
    })

    this.statusTarget.textContent = translate(labels.zoom_hint, { step: formatStep(data.step, labels) })
  }

  // [{ key, values, color, band }]: the *_max of a band series right after it
  plotSeries(config, data, series) {
    return config.series.flatMap((item) => {
      const result = [{ ...item, values: series.cols[item.key] }]
      if (item.band && data[`${item.key}_max`]) result.push({ key: `${item.key}_max`, values: series.cols[`${item.key}_max`], color: 'band', bandOf: result.length })
      return result
    })
  }

  options(name, config, data, series, plot, throttled, colors) {
    const labels = this.labelsValue
    const locale = this.localeValue
    const cores  = config.cores ? (data.totals || {}).cpu_cores : null
    const axis   = { stroke: colors.muted, font: '11px Poppins, system-ui, sans-serif', ticks: { show: false }, grid: { stroke: colors.grid, width: 1 } }
    const bands  = []

    const uSeries = [{}].concat(plot.map((item, index) => {
      if (item.color === 'band') {
        bands.push({ series: [index + 1, item.bandOf], fill: colors.band })
        return { label: labels.series[item.key], stroke: 'transparent', width: 0, points: { show: false } }
      }
      return { label: labels.series[item.key], stroke: colors[item.color], width: item.color === 's1' ? 2 : 1.5, points: { show: false } }
    }))

    return {
      width: this.chartWidth(name),
      height: CHART_HEIGHT,
      legend: { show: false },
      series: uSeries,
      bands,
      scales: {
        x: { time: true, range: () => [data.from, data.to] },
        y: { range: (u, dataMin, dataMax) => [config.min, config.max ?? this.niceMax(Math.max(dataMax ?? 0, cores ? cores + 0.5 : 0))] }
      },
      axes: [
        { ...axis, grid: { show: false }, space: 90, size: 46, values: (u, splits) => splits.map((value) => this.formatAxisTime(value, data.to - data.from)) },
        { ...axis, size: 44, values: (u, splits) => splits.map((value) => formatNumber(value, Number.isInteger(value) ? 0 : 1, locale)) }
      ],
      cursor: {
        sync: { key: 'abox-load' },
        drag: { x: true, y: false, setScale: false },
        y: false,
        points: { size: 7, width: 2, fill: colors.card }
      },
      hooks: {
        init: [(u) => this.initTooltip(u, name)],
        setCursor: [(u) => this.updateTooltip(u, name, plot, data)],
        setSelect: [(u) => this.zoom(u)],
        drawClear: [(u) => this.drawOffline(u, series.gaps, colors, config.offlineLabel)],
        draw: [(u) => this.drawMarks(u, colors, config.throttling ? throttled : [], cores)]
      }
    }
  }

  renderLegend(name, config, data, throttled) {
    const legend = this.legendTargets.find((target) => target.dataset.chart === name)
    if (!legend) return

    const labels = this.labelsValue
    const item   = (swatch, label, value = '') => `<span class="d-inline-flex align-items-center gap-1"><span class="abox-load__swatch ${swatch}"></span>${escapeHtml(label)} <strong>${escapeHtml(value)}</strong></span>`
    const items  = config.series.flatMap((series) => {
      const result = [item(series.color === 's2' ? 'is-s2' : '', labels.series[series.key], this.formatValue(series.key, lastValue(data, series.key)))]
      if (series.band && data[`${series.key}_max`]) result.push(item('is-band', labels.series[`${series.key}_max`], this.formatValue(`${series.key}_max`, lastValue(data, `${series.key}_max`))))
      return result
    })
    if (config.throttling) items.push(item('is-mark', labels.series.throttling, String(throttled.length)))
    if (config.cores && (data.totals || {}).cpu_cores) items.push(item('is-dashed', translate(labels.series.cores, { count: data.totals.cpu_cores })))

    legend.innerHTML = items.join('')
  }

  initTooltip(u, name) {
    const tooltip = document.createElement('div')
    tooltip.className = 'abox-load__tooltip'
    tooltip.hidden = true
    u.root.parentNode.appendChild(tooltip)
    u.tooltip = tooltip

    u.over.addEventListener('mouseenter', () => { this.hovered = name })
    u.over.addEventListener('mouseleave', () => {
      if (this.hovered === name) this.hovered = null
      tooltip.hidden = true
    })
  }

  // Only the chart under the mouse shows its values, the others show the synced crosshair
  updateTooltip(u, name, plot, data) {
    const index = u.cursor.idx
    if (this.hovered !== name || index == null || u.data[0][index] == null) {
      u.tooltip.hidden = true
      return
    }

    const rows = plot.map((item, position) => {
      const swatch = { s1: '', s2: 'is-s2', band: 'is-band' }[item.color]
      return `<div class="abox-load__tooltip-row"><span><span class="abox-load__swatch ${swatch}"></span>${escapeHtml(this.labelsValue.series[item.key])}</span><strong>${escapeHtml(this.formatValue(item.key, u.data[position + 1][index]))}</strong></div>`
    })
    u.tooltip.innerHTML = `<div class="abox-load__tooltip-time">${escapeHtml(this.formatTooltipTime(u.data[0][index], data.step))}</div>${rows.join('')}`
    u.tooltip.hidden = false

    // Next to the crosshair, on the side with more room
    const left = u.over.offsetLeft + u.cursor.left
    const rightSide = u.cursor.left < u.over.clientWidth / 2
    u.tooltip.style.left  = rightSide ? `${left + 12}px` : ''
    u.tooltip.style.right = rightSide ? '' : `${u.root.clientWidth - left + 12}px`
  }

  zoom(u) {
    const { left, width } = u.select
    u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false)
    if (width < MIN_SELECTION_PX) return

    this.presetTargets.forEach((button) => button.classList.remove('active'))
    this.load(u.posToVal(left, 'x'), u.posToVal(left + width, 'x'))
  }

  // Shaded periods without data (the box was offline)
  drawOffline(u, gaps, colors, withLabel) {
    const { ctx } = u
    const { top, height } = u.bbox
    const ratio = window.devicePixelRatio || 1

    ctx.save()
    gaps.forEach(([from, to]) => {
      const x0 = u.valToPos(from, 'x', true)
      const x1 = u.valToPos(to, 'x', true)
      ctx.fillStyle = colors.offline
      ctx.fillRect(x0, top, x1 - x0, height)

      if (withLabel && x1 - x0 > 70 * ratio) {
        ctx.fillStyle = colors.muted
        ctx.font = `${10 * ratio}px Poppins, system-ui, sans-serif`
        ctx.textAlign = 'center'
        ctx.fillText(this.labelsValue.offline, (x0 + x1) / 2, top + 14 * ratio)
      }
    })
    ctx.restore()
  }

  // Throttling ticks on top, the CPU cores line on the load chart
  drawMarks(u, colors, throttled, cores) {
    const { ctx } = u
    const { left, top, width } = u.bbox
    const ratio = window.devicePixelRatio || 1

    ctx.save()
    ctx.fillStyle = colors.warnMark
    throttled.forEach((ts) => ctx.fillRect(u.valToPos(ts, 'x', true) - 1.5 * ratio, top, 3 * ratio, 10 * ratio))

    if (cores) {
      const y = u.valToPos(cores, 'y', true)
      ctx.strokeStyle = colors.muted
      ctx.lineWidth = ratio
      ctx.setLineDash([4 * ratio, 4 * ratio])
      ctx.beginPath()
      ctx.moveTo(left, y)
      ctx.lineTo(left + width, y)
      ctx.stroke()
    }
    ctx.restore()
  }

  resizeCharts() {
    Object.entries(this.plots).forEach(([name, plot]) => plot.setSize({ width: this.chartWidth(name), height: CHART_HEIGHT }))
  }

  destroyCharts() {
    Object.values(this.plots || {}).forEach((plot) => {
      plot.tooltip?.remove()
      plot.destroy()
    })
    this.plots = {}
  }

  // Data

  // Columns with a null row after each gap longer than 2 steps (the box was offline), so lines break there
  withGaps(data) {
    const t = [], gaps = []
    const cols = Object.fromEntries(KEYS.map((key) => [key, []]))

    data.t.forEach((ts, index) => {
      const previous = data.t[index - 1]
      if (index > 0 && ts - previous > data.step * 2) {
        gaps.push([previous + data.step, ts])
        t.push(previous + data.step)
        KEYS.forEach((key) => cols[key].push(null))
      }
      t.push(ts)
      KEYS.forEach((key) => cols[key].push(data[key] ? data[key][index] : null))
    })

    return { t, cols, gaps }
  }

  // Start times of the throttled runs
  throttled(data) {
    const freq = data.cpu_freq || []
    const top  = max(freq)
    if (!top) return []

    const result = []
    data.t.forEach((ts, index) => {
      const slow = freq[index] != null && freq[index] < top * THROTTLED_FREQ && (data.cpu[index] || 0) >= BUSY_CPU
      const previousSlow = index > 0 && freq[index - 1] != null && freq[index - 1] < top * THROTTLED_FREQ && (data.cpu[index - 1] || 0) >= BUSY_CPU
      if (slow && !previousSlow) result.push(ts)
    })
    return result
  }

  // Helpers

  colors() {
    const style = getComputedStyle(this.element)
    const value = (name) => style.getPropertyValue(name).trim()
    return {
      s1: value('--chart-s1'), s2: value('--chart-s2'), band: value('--chart-band'), grid: value('--chart-grid'),
      muted: value('--chart-muted'), offline: value('--chart-offline'), warnMark: value('--chart-warn-mark'), card: value('--color-white')
    }
  }

  formatValue(key, value) {
    if (value == null) return '—'
    const unit = UNITS[key] || ({ net_rx: ` ${this.labelsValue.units.mbit}`, net_tx: ` ${this.labelsValue.units.mbit}`, disk_read: ` ${this.labelsValue.units.mb}`, disk_write: ` ${this.labelsValue.units.mb}` }[key] || '')
    return `${formatNumber(value, DIGITS[key] ?? 1, this.localeValue)}${unit}`
  }

  formatAxisTime(seconds, period) {
    const date   = new Date(seconds * 1000)
    const locale = browserLocale(this.localeValue)
    if (period <= 86400) return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    // date over time: two lines, uPlot splits axis values by \n
    if (period <= 7 * 86400) return `${date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}\n${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
  }

  formatTooltipTime(seconds, step) {
    const time = new Date(seconds * 1000).toLocaleString(browserLocale(this.localeValue), {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: step < 60 ? '2-digit' : undefined
    })
    return step >= 60 ? `${time} · ${formatStep(step, this.labelsValue)}` : time
  }

  // 10% headroom, rounded up to a half of the value's magnitude: 75 -> 85, 3.2 -> 4
  niceMax(value) {
    if (!value) return 1
    const half = 10 ** Math.floor(Math.log10(value)) / 2
    return Math.ceil(value * 1.1 / half) * half
  }

  chartWidth(name) {
    const target = this.chartTargets.find((chart) => chart.dataset.chart === name)
    return Math.max(target ? target.clientWidth : 0, 200)
  }

  parseDate(value) {
    const time = value && new Date(value.replace(' ', 'T')).getTime()
    return time ? Math.floor(time / 1000) : null
  }

  isDark() {
    return document.body.classList.contains('dark-theme')
  }

  now() {
    return Math.floor(Date.now() / 1000)
  }
}
